import { Fragment, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { NAV_ITEMS } from "@/lib/nav";
import { FundioLogo } from "@/components/FundioLogo";
import { useAuth } from "@/context/AuthContext";
import { useT52 } from "@/i18n/v52";

const SECTION_KEYS = {
  "Kunden & Brockis": "customers", "Inhalte": "content", "Business": "business",
  "Verkauf & Wachstum": "sales", "Admin": "admin", "System": "system", "Sicherheit": "security",
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const isActivePath = (path, pathname) => (path === "/" ? pathname === "/" : pathname.startsWith(path));

// RBAC-Filter unverändert aus der bisherigen Sidebar.
export const canSeeNav = (user, i) =>
  i.type === "module" ? !!user?.permissions?.[i.module]?.read
    : i.type === "superadmin" ? user?.role === "super_admin"
      : i.type === "review" ? ["super_admin", "admin_ops"].includes(user?.role)
        : user?.role === "super_admin" || user?.role === "kewag_admin";

function useVisibleGroups() {
  const { user } = useAuth();
  return useMemo(() => {
    const items = NAV_ITEMS.filter((i) => canSeeNav(user, i));
    const groups = [];
    items.forEach((it) => {
      const last = groups[groups.length - 1];
      if (last && last.section === (it.section || null)) last.items.push(it);
      else groups.push({ section: it.section || null, items: [it] });
    });
    return groups;
  }, [user]);
}

export function NavGroups({ prefix = "", onNavigate }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const groups = useVisibleGroups();
  const activeSection = groups.find((g) => g.items.some((i) => isActivePath(i.path, pathname)))?.section;
  const [open, setOpen] = useState(() => new Set(["Kunden & Brockis", activeSection].filter(Boolean)));
  useEffect(() => { if (activeSection) setOpen((s) => (s.has(activeSection) ? s : new Set(s).add(activeSection))); }, [activeSection]);
  const toggle = (sec) => setOpen((s) => { const n = new Set(s); n.has(sec) ? n.delete(sec) : n.add(sec); return n; });

  const link = (item) => {
    const Icon = item.icon;
    return (
      <li key={item.key}>
        <NavLink to={item.path} end={item.path === "/"} onClick={onNavigate} data-testid={`${prefix}${item.testid}`}
          className={({ isActive }) => `f52-nav-item${isActive ? " is-active" : ""}`}>
          <Icon aria-hidden="true" /><span>{t(`nav.items.${item.key}`)}</span>
        </NavLink>
      </li>
    );
  };

  return groups.map((g) => {
    if (!g.section) return <ul key="top" className="f52-nav-list">{g.items.map(link)}</ul>;
    const id = `${prefix}navgrp-${slug(g.section)}`;
    const expanded = open.has(g.section);
    return (
      <Fragment key={g.section}>
        <button type="button" className="f52-nav-group" aria-expanded={expanded} aria-controls={id}
          onClick={() => toggle(g.section)} data-testid={`${prefix}nav-section-${slug(g.section)}`}>
          {SECTION_KEYS[g.section] ? t(`nav.sections.${SECTION_KEYS[g.section]}`) : g.section}
          <ChevronDown aria-hidden="true" />
        </button>
        <ul id={id} className="f52-nav-list" hidden={!expanded}>{g.items.map(link)}</ul>
      </Fragment>
    );
  });
}

export function Sidebar() {
  const { env } = useAuth();
  const { t } = useTranslation();
  const { t52 } = useT52();
  const isProd = !!env?.is_production;
  return (
    <aside data-testid="app-sidebar" className="f52-sidebar" aria-label={t52("mainNav")}>
      <div className="f52-sb-logo"><FundioLogo variant="weiss" height={30} /></div>
      <nav className="f52-nav" aria-label={t52("mainNav")}><NavGroups /></nav>
      <div className="f52-sb-foot">
        {t("layout.environment")}
        <strong data-testid="sidebar-env-label" className={isProd ? "is-prod" : ""}>
          {t(isProd ? "layout.liveProduction" : "layout.previewStaging")}
        </strong>
      </div>
    </aside>
  );
}
